<template>
  <section class="vehicle-header" role="region" aria-labelledby="vehicle-title">
    <div v-if="loading" class="vehicle-header__card vehicle-header__skeleton">
      <div class="vehicle-header__media skeleton"></div>
      <div class="vehicle-header__content">
        <div class="skeleton skeleton-line" style="width: 120px"></div>
        <div class="skeleton skeleton-line xl" style="width: 240px"></div>
        <div class="skeleton skeleton-line" style="width: 180px"></div>
        <div class="skeleton skeleton-line" style="width: 220px"></div>
      </div>
    </div>

    <div v-else class="vehicle-header__card">
      <div class="vehicle-header__media">
        <img v-if="cover" :src="cover" :alt="title" />
        <div v-else class="vehicle-header__placeholder">
          <span>{{ typeLabel }}</span>
        </div>
      </div>

      <div class="vehicle-header__content">
        <div class="vehicle-header__title-row">
          <div>
            <p class="vehicle-header__eyebrow">Vehículo</p>
            <h1 id="vehicle-title">{{ title }}</h1>
          </div>
          <button v-if="showEdit" class="vehicle-header__edit" @click="$emit('edit')">
            Editar
          </button>
        </div>
        <div class="vehicle-header__meta">
          <div class="vehicle-header__meta-item">
            <span>Matrícula</span>
            <strong>{{ plate || "—" }}</strong>
          </div>
          <div class="vehicle-header__meta-item">
            <span>Año</span>
            <strong>{{ year || "—" }}</strong>
          </div>
          <div class="vehicle-header__meta-item">
            <span>VIN</span>
            <strong>{{ vin || "—" }}</strong>
          </div>
        </div>
        <div class="vehicle-header__status">
          <UiBadge :tone="statusTone">{{ statusLabel }}</UiBadge>
          <p class="vehicle-header__status-detail">{{ statusDetail }}</p>
        </div>
        <div class="vehicle-header__tags" v-if="tags.length">
          <UiBadge v-for="tag in tags" :key="tag.label" :tone="tag.tone">{{ tag.label }}</UiBadge>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import UiBadge from "../ui/UiBadge.vue";

export default {
  name: "VehicleHeader",
  components: { UiBadge },
  props: {
    vehicle: { type: Object, default: null },
    cover: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    showEdit: { type: Boolean, default: true },
    status: {
      type: Object,
      default: () => ({ label: "—", detail: "", tone: "neutral" }),
    },
    tags: { type: Array, default: () => [] },
  },
  computed: {
    title() {
      const brand = this.vehicle?.brand || "";
      const model = this.vehicle?.model || "";
      return `${brand} ${model}`.trim() || "Vehículo";
    },
    plate() {
      return this.vehicle?.plate || "";
    },
    year() {
      return this.vehicle?.registrationYear || this.vehicle?.registration_year || "";
    },
    vin() {
      return this.vehicle?.vin || "";
    },
    statusLabel() {
      return this.status?.label || "—";
    },
    statusDetail() {
      return this.status?.detail || "";
    },
    statusTone() {
      return this.status?.tone || "neutral";
    },
    typeLabel() {
      const type = this.vehicle?.type;
      const map = {
        CAR: "Coche",
        MOTORCYCLE: "Moto",
        VAN: "Furgoneta",
        TRUCK: "Camión",
        OTHER: "Vehículo",
      };
      return map[type] || "Vehículo";
    },
  },
};
</script>

<style scoped>
.vehicle-header__card {
  display: grid;
  grid-template-columns: minmax(160px, 220px) 1fr;
  gap: 20px;
  padding: 22px;
  border-radius: 24px;
  background: var(--color-card);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
}

.vehicle-header__media {
  border-radius: 18px;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.2);
  display: grid;
  place-items: center;
  min-height: 160px;
}

.vehicle-header__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.vehicle-header__placeholder {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: var(--muted);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 12px;
}

.vehicle-header__content {
  display: grid;
  gap: 12px;
  align-content: start;
}

.vehicle-header__title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.vehicle-header__edit {
  align-self: flex-start;
  border: 1px solid rgba(56, 189, 248, 0.35);
  background: rgba(56, 189, 248, 0.12);
  color: var(--text);
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.vehicle-header__edit:hover {
  border-color: rgba(56, 189, 248, 0.7);
  background: rgba(56, 189, 248, 0.2);
  transform: translateY(-1px);
}

.vehicle-header__edit:active {
  transform: translateY(0);
}

.vehicle-header__eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.32em;
  font-size: 11px;
  color: var(--muted);
  margin: 0;
}

.vehicle-header h1 {
  margin: 0;
  font-size: 30px;
  letter-spacing: -0.02em;
}

.vehicle-header__meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}

.vehicle-header__meta-item {
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 14px;
  padding: 10px 12px;
  display: grid;
  gap: 4px;
}

.vehicle-header__meta-item span {
  font-size: 12px;
  color: var(--muted);
}

.vehicle-header__meta-item strong {
  font-size: 14px;
  font-weight: 600;
}

.vehicle-header__status {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.vehicle-header__status-detail {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}

.vehicle-header__tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.vehicle-header__skeleton {
  align-items: center;
}

@media (max-width: 900px) {
  .vehicle-header__card {
    grid-template-columns: 1fr;
  }

  .vehicle-header h1 {
    font-size: 26px;
  }
}
</style>
